import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import {
  Alert,
  Box,
  Collapse,
  Container,
  CssBaseline,
  Divider,
  Fab,
  Grid,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState, useCallback } from 'react';
import ReactGA from 'react-ga4';
import { Route, Routes } from 'react-router-dom';
import { Slide, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import './App.css';
import AgentDialog from './components/AgentDialog';
import Footer from './components/Footer';
import { HomeIcon } from './components/HomeIcon';
import InfoBox from './components/InfoBox';
import InsuranceStep from './components/InsuranceStep';
import NoticeDialog from './components/NoticeDialog';
import ScrollTop from './components/ScrollToTop';
import AppConsts from './config/appconst';
import allMandants from './config/mandant';
import mandantCards from './config/mandantCrads';
import { getMandantTheme } from './config/mandantTheme';
import { getTheme } from './config/theme';
import Confirmation from './pages/confirmation';
import Contribution from './pages/contribution';
import DeeplinkCreator from './pages/deeplinkcreator/DeeplinkCreator';
import Insuredperson from './pages/insuredperson';
import InsuranceSummary from './pages/summary';
import { setConfig } from './store/reducers/config.reducer';
import {
  base64ToUtf8,
  getQueryParams,
  normalizeStreet,
  resolveMandant,
} from './utils';
import { useAppDispatch, useAppSelector } from './utils/hooks';
import customQuestions from '../src/config/customQuestions.json';
import customFields from 'src/config/customFields.json';
import { availableMandants } from './utils/constants';
import LoginPage from './pages/admin/LoginPage';
import AdminPage from './pages/admin/AdminPage';
import ProtectedRoute from './components/ProtectedRoute';
import { verifySessionThunk } from './store/thunks/auth.thunk';
import { getToken, checkAgent } from './api/AdminAPI';

const defaultMandant = process.env.REACT_APP_MANDANT_DEFAULT as string;
const defaultAgentId = AppConsts.agentNumber1;
const defaultAgentId2 = AppConsts.agentNumber2;

const queryParams = getQueryParams();

type ComponentKey = 0 | 1 | 2 | 3;
const getComponent: Record<ComponentKey, React.ReactNode> = {
  0: <Contribution />,
  1: <Insuredperson />,
  2: <InsuranceSummary />,
  3: <Confirmation />,
};

function App() {
  const [open, setOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(true);
  const [agentBlocked, setAgentBlocked] = useState(false);
  const [agentCheckLoading, setAgentCheckLoading] = useState(false);
  // const [showBackdrop, setShowBackdrop] = useState(true);

  const [isAccessibilityMode, setIsAccessibilityMode] = useState<boolean>(
    () => {
      return localStorage.getItem('accessibilityMode') === 'true';
    }
  );

  const { steps } = useAppSelector((state) => state.stepper);
  const mandant = resolveMandant(defaultMandant);
  const agentId = queryParams.get('agentId1') ?? defaultAgentId;
  const agentId2 = queryParams.get('agentId2') ?? defaultAgentId2;

  const contactInformation = queryParams.get('contactInformation');
  const customValuesInformation = queryParams.get('customValues');
  const askForGewerkschaft = queryParams.get('askForGewerkschaft') === 'true';

  const dispatch = useAppDispatch();
  const currentConfig = useAppSelector(
    (state: { config: ConfigState }) => state.config
  );
  const theme = currentConfig.theme;
  const { isTariffClicked } = useAppSelector((state) => state.contribution);

  type MandantKey = keyof typeof allMandants;
  const mandants = useMemo(() => {
    const activatedMandants =
      process.env.REACT_APP_ACTIVATED_MANDANTS?.split(',');
    const filteredMandants: { [key: string]: any } = {};

    activatedMandants?.forEach((key) => {
      if (allMandants.hasOwnProperty(key)) {
        filteredMandants[key] = allMandants[key as MandantKey];
      }
    });

    return filteredMandants;
  }, []);

  const agentNumbers: string[] =
    mandants[mandant as MandantKey]?.allowedAgentNumbers;

  const allowAgentInfoBox = mandants[mandant as MandantKey]?.allowAgentInfoBox;
  const footer = mandants[mandant as MandantKey]?.footer;

  const agentData = useMemo(() => {
    if (!contactInformation) return {};
    const parsed = JSON.parse(base64ToUtf8(contactInformation));
    if (parsed.street) {
      parsed.street = normalizeStreet(parsed.street);
    }
    return parsed;
  }, [contactInformation]);

  const customValues = useMemo(
    () =>
      customValuesInformation
        ? JSON.parse(base64ToUtf8(customValuesInformation))
        : {},
    [customValuesInformation]
  );

  const isValidMandant = useCallback((value: any): value is Mandant => {
    return availableMandants.includes(value);
  }, []);
  useEffect(() => {
    let key = mandants?.hasOwnProperty(mandant) ? mandant : defaultMandant;
    type MandantKeyFiltred = keyof typeof mandants;
    let conf = mandants?.[key as MandantKeyFiltred];

    const allowAccessibilityMode = conf?.allowAccessibilityMode ?? false;

    const accessibilityMode = allowAccessibilityMode
      ? localStorage.getItem('accessibilityMode') === 'true'
      : false;

    if (!allowAccessibilityMode) {
      localStorage.setItem('accessibilityMode', 'false');
    }

    let themeMandant = getMandantTheme(key, accessibilityMode);

    if (
      agentNumbers &&
      agentNumbers.length > 0 &&
      !agentNumbers.includes(agentId) &&
      !agentNumbers.includes(agentId2)
    ) {
      setOpen(true);
    }

    const agentIDs =
      agentId2 !== ''
        ? { agentNumber1: agentId, agentNumber2: agentId2 }
        : { agentNumber1: agentId };

    dispatch(
      setConfig({
        ...conf,
        theme: themeMandant,
        mandant: key,
        isColorBlindMode: accessibilityMode,
        agentId,
        disableDataProtectionBox: isValidMandant(key)
          ? mandants[key as MandantKeyFiltred]?.disableDataProtectionBox ??
            false
          : false,
        customQuestions: isValidMandant(key) ? customQuestions[key] : [],
        customFields: isValidMandant(key) ? customFields[key] : [],
        customValues,
        agentData: {
          ...agentIDs,
          ...agentData,
        },
        askForGewerkschaft,
        gewerkschaftsnummer: '',
      } as ConfigState)
    );

    setIsAccessibilityMode(accessibilityMode);
  }, [
    mandant,
    agentId,
    agentId2,
    agentData,
    dispatch,
    customValues,
    mandants,
    isValidMandant,
    agentNumbers,
  ]);

  useEffect(() => {
    if (getToken()) {
      dispatch(verifySessionThunk());
    }
  }, [dispatch]);

  const urlAgentId2 = queryParams.get('agentId2');

  useEffect(() => {
    if (mandant === 'sdk' && agentId) {
      setAgentCheckLoading(true);
      checkAgent(agentId, urlAgentId2 || undefined)
        .then((res) => {
          setAgentBlocked(res.data.blocked);
        })
        .catch(() => {
          setAgentBlocked(false);
        })
        .finally(() => {
          setAgentCheckLoading(false);
        });
    }
  }, [mandant, agentId, urlAgentId2]);

  useEffect(() => {
    if (process.env.REACT_APP_ENV === 'production') {
      ReactGA.pageview(window.location.pathname + window.location.search);
    }
  }, []);

  type MandantCards = keyof typeof mandantCards;
  const infoBoxOnStartpage: string | null =
    mandants[mandant as MandantKey]?.infoBoxOnStartpage;
  const InfoBoxComponent =
    infoBoxOnStartpage !== null
      ? mandantCards[infoBoxOnStartpage as MandantCards]
      : null;

  // const handleBackdropClick = () => {
  //   // Handle backdrop click logic here
  //   setShowBackdrop(false);
  // };

  const handleThemeToggle = () => {
    const allowAccessibilityMode =
      mandants[mandant as MandantKey]?.allowAccessibilityMode;

    if (!allowAccessibilityMode) return;

    const newValue = !isAccessibilityMode;
    setIsAccessibilityMode(newValue);
    localStorage.setItem('accessibilityMode', String(newValue));

    const key = mandants?.hasOwnProperty(mandant) ? mandant : defaultMandant;
    const newTheme = getMandantTheme(key, newValue);

    dispatch(
      setConfig({
        ...currentConfig,
        isColorBlindMode: newValue,
        theme: newTheme,
      })
    );
  };

  return (
    <ThemeProvider theme={getTheme(theme)}>
      <CssBaseline />
      <ToastContainer transition={Slide} />
      {/* {showBackdrop && (
        <Box
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            transition: 'opacity 0.5s ease',
            opacity: 1,
          }}
          onClick={handleBackdropClick}
        />
      )} */}
      <Routes>
        <Route
          path="/"
          element={
            <Box>
              {agentBlocked && !agentCheckLoading && (
                <Container sx={{ mt: 8, textAlign: 'center' }}>
                  <Alert severity="error" sx={{ mb: 2, justifyContent: 'center' }}>
                    <Typography variant="h6">
                      Die Anwendung steht nicht mehr zur Verfügung.
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      Bitte wenden Sie sich an Ihren Ansprechpartner.
                    </Typography>
                  </Alert>
                </Container>
              )}
              {!agentBlocked && !agentCheckLoading && process.env.REACT_APP_ENV !== 'production' && (
                <NoticeDialog open={openDialog} setOpen={setOpenDialog} />
              )}
              {!agentBlocked && !agentCheckLoading && (process.env.REACT_APP_ENV !== 'production'
                ? open &&
                  !openDialog && <AgentDialog open={open} setOpen={setOpen} />
                : open && <AgentDialog open={open} setOpen={setOpen} />)}
              {!agentBlocked && !agentCheckLoading && <>
              <div id="back-to-top-anchor"></div>
              <Container sx={{ paddingBottom: '80px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: '45%', sm: '70%' },
                      ...(mandant === 'sdk' && { height: { xs: '100px' ,sm: '130px' } }),
                    }}
                  >
                    <HomeIcon />
                  </Box>
                  {mandants[mandant as MandantKey]?.allowAccessibilityMode && (
                    <>
                      <Typography
                        variant="h5"
                        sx={{
                          padding: '4px 0',
                          fontSize: { xs: '12px', sm: '16px' },
                          fontWeight: 400,
                        }}
                      >
                        Barrierefreiheitsmodus{' '}
                        <Box
                          component="span"
                          sx={{
                            textDecoration: isAccessibilityMode
                              ? 'underline'
                              : 'underline',
                            cursor: 'pointer',
                            fontWeight: 700,
                            color: isAccessibilityMode
                              ? 'accessibility.link.main'
                              : 'primary.main',
                          }}
                          onClick={handleThemeToggle}
                        >
                          {isAccessibilityMode ? 'ausschalten' : 'einschalten'}
                        </Box>
                      </Typography>
                      <Box
                        onClick={handleThemeToggle}
                        sx={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          ml: 1,
                        }}
                      >
                        {' '}
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background: isAccessibilityMode
                              ? 'linear-gradient(90deg, #fff 50%, #000 50%)'
                              : (theme) =>
                                  `linear-gradient(90deg, ${theme.palette.primary.main} 50%, #fff 50%)`,

                            border: isAccessibilityMode
                              ? '1px solid #000'
                              : (theme) =>
                                  `1px solid ${theme.palette.primary.main}`,

                            transition: 'background 0.3s ease',
                          }}
                        />
                      </Box>
                    </>
                  )}
                </Box>

                {mandant === 'sdk' && isAccessibilityMode && steps === 0 && (
                  <Grid container sx={{ marginBottom: 0, marginTop: -1 }}>
                    <Alert
                      severity="info"
                      sx={{
                        width: '100%',
                        alignItems: 'center',
                        backgroundColor: '#fff',
                        height: { xs: 'auto', md: '138px' },
                        color: 'black',
                        borderRadius: '8px',
                        border: '1px solid #000000',
                        '& .MuiAlert-icon': {
                          color: 'black',
                        },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          color: 'black',
                          fontWeight: '400',
                          lineHeight: '1.2',
                          fontSize: { xs: '12px', sm: '14px' },
                        }}
                      >
                        Hinweis: <br />
                        Die folgenden Texte sind in leichter Sprache. Manche
                        Wörter sind deshalb vielleicht nicht ganz genau. Wenn
                        Sie genaue Wörter wollen, lesen Sie bitte die normalen
                        Texte und die Vertrags-Unterlagen, die Sie nachher
                        herunterladen. Oder fragen Sie uns.
                        <br /> Wenn wir Ihnen etwas erklären, sehen Sie diese
                        Erklärungen in{' '}
                        <Box component="span" sx={{ color: '#0066B3' }}>
                          <span style={{ fontWeight: 'bold' }}>
                            blauer Farbe{' '}
                          </span>
                          (die Erklärungen können sie sehen, wenn sie auf das
                          (i) klicken).
                        </Box>{' '}
                        Wenn es sich um rechtsverbindliche Erklärungen von Ihnen
                        handelt, sind diese <strong>fettgedruckt</strong>.
                        Rechtsverbindlich bedeutet, dass die Erklärungen genau
                        so eingehalten werden müssen. Es ist wie ein
                        Versprechen, das man nicht brechen darf. Man muss sich
                        daran halten, was hier steht.
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                {InfoBoxComponent !== null ? (
                  <Box>
                    <div>
                      <Collapse
                        in={!isTariffClicked}
                        orientation={'vertical'}
                        easing={'timeout'}
                      >
                        {InfoBoxComponent}
                      </Collapse>
                    </div>
                    {isTariffClicked && (
                      <>
                        <InsuranceStep />
                        <Divider
                          sx={{
                            borderColor: 'primary.main',
                          }}
                        />
                      </>
                    )}
                  </Box>
                ) : (
                  <>
                    <InsuranceStep />
                    {steps === 0 ||
                      (steps === 3 && (
                        <Divider
                          sx={{
                            borderColor: 'primary.main',
                          }}
                        />
                      ))}
                  </>
                )}
                <Box sx={{ pb: 10 }}>{getComponent[steps as ComponentKey]}</Box>
              </Container>
              {allowAgentInfoBox && steps !== 3 && <InfoBox />}
              <ScrollTop>
                <Fab
                  color="primary"
                  size="small"
                  aria-label="scroll back to top"
                >
                  <ArrowUpwardIcon />
                </Fab>
              </ScrollTop>
              </>}
            </Box>
          }
        />
        <Route path="/deeplink" element={<DeeplinkCreator />} />
        {mandant === 'sdk' && (
          <>
            <Route path="/admin/login" element={<LoginPage />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              }
            />
          </>
        )}
      </Routes>
      <Footer
        leftText1={footer?.imprint}
        leftText2={footer?.dataprotection}
        leftText3={footer?.serviceHours}
        companyText={footer?.companyText}
      />
    </ThemeProvider>
  );
}

export default App;
